/**
 * `/email-templates` — every email this studio sends, in one place.
 *
 * Five kinds, from three reads:
 *
 *   - Transactional, marketing and sign-in emails have a template, listed by
 *     `GET /api/email-templates`. "View" asks the server for the email as a
 *     member would get it (`…/:kind/preview`): it is built by the code that
 *     sends it, so a version the studio saved is shown where a send uses it
 *     and the popup says what it is showing (`previewSubtitle`). A row with a
 *     saved version is tagged "edited". A flow's emails carry the flow's
 *     on/off state, read fresh through `list_marketing_flows` — the same
 *     action every other surface uses.
 *   - Staff alerts are plain text written by each sender, so there is nothing
 *     to preview: the page lists which alert types can arrive by email and how
 *     many people one would email right now (`GET /api/email-templates/live`).
 *   - Newsletters are sent by hand from Resend. They are listed from Resend's
 *     delivery reports (same endpoint) and opened from one delivered copy
 *     through `read_resend_email`, with every link switched off.
 *
 * Each read fails on its own: a section that cannot load says so and the rest
 * of the page still renders. "View" opens a sandboxed iframe — the same safe
 * pattern as EmailHtmlModal (no scripts, no same-origin; images still load).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { studioFormat } from "../lib/studio-tz";
import { NOTIFICATION_GRID_TYPES } from "../lib/notification-grid";
import {
  disableLinks,
  flowSwitch,
  groupMarketingByFlow,
  NEWSLETTER_TEST_SEND_BELOW,
  peopleCount,
  previewDocument,
  previewFailureText,
  previewHeading,
  previewSubject,
  previewSubtitle,
  splitNewsletters,
  staffAlertRows,
  textToHtmlDocument,
  type EmailEntry,
  type EmailPreviewResponse,
  type FlowSwitch,
  type ListedFlow,
  type Newsletter,
  type StaffAlertCount,
} from "../lib/email-inventory";
import { EmailPreviewModal } from "../components/EmailPreviewModal";

interface TemplatesResponse {
  readonly templates: ReadonlyArray<EmailEntry>;
  /** False when the studio's flow steps could not be read (see the notice). */
  readonly flow_steps_read: boolean;
  /** False when the studio's saved edits could not be read: no row is tagged. */
  readonly saved_edits_read: boolean;
}

interface LiveResponse {
  readonly staff_alerts: ReadonlyArray<StaffAlertCount>;
  readonly newsletters: ReadonlyArray<Newsletter>;
}

interface ListFlowsResponse {
  readonly flows: ReadonlyArray<ListedFlow>;
}

interface ReadResendEmailResponse {
  readonly message: {
    readonly body_text: string | null;
    readonly body_html: string | null;
  };
}

interface Preview {
  readonly title: string;
  readonly subtitle: string;
  /** The subject line the email goes out under, when the server knows it. */
  readonly subject?: string;
  readonly html: string;
}

const LAYOUT_LABEL: Record<NonNullable<EmailEntry["layout"]>, string> = {
  design: "Own design",
  generic: "Shared layout",
  plain_html: "Plain, with links",
  plain: "Plain text",
};

const SENT_DATE: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

function errorText(prefix: string, e: unknown): string {
  return e instanceof ApiError
    ? `${prefix}:${String(e.status)}`
    : (e as Error).message;
}

/** The on/off state of the flow that sends an email, as `/flows` words it. */
function FlowSwitchTags({ state }: { state: FlowSwitch }): JSX.Element {
  if (state.on === null) {
    return (
      <span
        className="tag"
        title="The flow list could not be read, so its on/off state is unknown here. See /flows."
      >
        unknown
      </span>
    );
  }
  return (
    <>
      <span className={state.on ? "tag tag-success" : "tag tag-error"}>
        {state.on ? "ON" : "OFF"}
      </span>
      {state.notSet ? (
        <span
          className="tag"
          title="Nobody has switched this flow on or off, so this is its shipped default — and the automatic enrollers treat a flow nobody switched as OFF. Switching it once on /flows records a decision."
        >
          not set
        </span>
      ) : null}
    </>
  );
}

/** Marks an email the studio has saved its own version of. */
function EditedTag(): JSX.Element {
  return (
    <span
      className="tag"
      title="This studio has saved its own version of this email. View shows whether that is the version being sent."
    >
      edited
    </span>
  );
}

export function EmailTemplates(): JSX.Element {
  const [list, setList] = useState<TemplatesResponse | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [flows, setFlows] = useState<ReadonlyArray<ListedFlow> | null>(null);
  const [live, setLive] = useState<LiveResponse | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Three independent reads: one failing must not blank the others.
    const listed = api
      .get<TemplatesResponse>("/api/email-templates")
      .then((resp) => {
        if (cancelled) return;
        setList(resp);
        setListError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setListError(errorText("api_error", e));
      });
    const flowStates = dispatchAction<Record<string, never>, ListFlowsResponse>(
      "list_marketing_flows",
      {},
    )
      .then((r) => {
        if (!cancelled) setFlows(r.ok ? r.response.flows : null);
      })
      .catch(() => {
        if (!cancelled) setFlows(null);
      });
    const liveRead = api
      .get<LiveResponse>("/api/email-templates/live")
      .then((resp) => {
        if (cancelled) return;
        setLive(resp);
        setLiveError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLiveError(errorText("api_error", e));
      });
    void Promise.all([listed, flowStates, liveRead]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onViewTemplate = useCallback(async (entry: EmailEntry) => {
    setOpening(entry.kind);
    setOpenError(null);
    try {
      const resp = await api.get<EmailPreviewResponse>(
        `/api/email-templates/${encodeURIComponent(entry.kind)}/preview`,
      );
      const subject = previewSubject(resp);
      setPreview({
        // The row's label, except over a step with no design, whose label
        // is its subject as written: there, the subject as filled.
        title: previewHeading(entry, resp),
        subtitle: previewSubtitle(resp, {
          // A design's row whose step the flow no longer has: its key is a
          // position, so the server shows whatever that position sends now.
          designLeftBehind: entry.layout === "design" && entry.in_flow === false,
        }),
        ...(subject !== undefined ? { subject } : {}),
        html: previewDocument(resp),
      });
    } catch (e) {
      setOpenError(
        e instanceof ApiError ? previewFailureText(e) : (e as Error).message,
      );
    } finally {
      setOpening(null);
    }
  }, []);

  const onViewNewsletter = useCallback(async (row: Newsletter) => {
    setOpening(row.broadcast_id);
    setOpenError(null);
    try {
      // A fresh request id per open (dispatchAction's default): the body is
      // PII-stripped on an idempotency replay (invariant 11).
      const r = await dispatchAction<
        { message_id: string },
        ReadResendEmailResponse
      >("read_resend_email", { message_id: row.sample_message_id });
      if (!r.ok) {
        setOpenError(
          r.code === "resend_message_not_found"
            ? "Resend no longer has a copy of this newsletter to show."
            : `Couldn't load the newsletter (${r.code}): ${r.message}`,
        );
        return;
      }
      const m = r.response.message;
      const html =
        m.body_html !== null && m.body_html.length > 0
          ? m.body_html
          : m.body_text !== null && m.body_text.length > 0
            ? textToHtmlDocument(m.body_text)
            : null;
      if (html === null) {
        setOpenError("Resend returned this newsletter without a body.");
        return;
      }
      setPreview({
        title: row.subject.length > 0 ? row.subject : "Newsletter",
        subtitle:
          "One recipient's copy, as delivered. Links are switched off: its unsubscribe link belongs to that recipient.",
        html: disableLinks(html),
      });
    } catch (e) {
      setOpenError((e as Error).message);
    } finally {
      setOpening(null);
    }
  }, []);

  const templates = list?.templates ?? null;
  const marketing = useMemo(
    () => groupMarketingByFlow(templates ?? []),
    [templates],
  );
  const alerts = useMemo(
    () => staffAlertRows(live?.staff_alerts ?? [], NOTIFICATION_GRID_TYPES),
    [live],
  );
  const newsletters = useMemo(
    () => splitNewsletters(live?.newsletters ?? []),
    [live],
  );
  const sentDate = useMemo(() => studioFormat(SENT_DATE), []);

  const viewButton = (entry: EmailEntry): JSX.Element => (
    <button
      type="button"
      className="row-action-btn"
      disabled={opening === entry.kind}
      onClick={() => void onViewTemplate(entry)}
    >
      {opening === entry.kind ? "Loading…" : "View"}
    </button>
  );

  const editLink = (entry: EmailEntry): JSX.Element => (
    <Link
      className="row-action-btn"
      style={{ marginLeft: "8px" }}
      to={`/email-templates/${entry.kind}/edit`}
    >
      Edit
    </Link>
  );

  /** A table of single templates (transactional, shared layout, sign-in). */
  const templateTable = (
    rows: ReadonlyArray<EmailEntry>,
    withSwitch: boolean,
  ): JSX.Element => (
    <table className="data-table table-cards">
      <thead>
        <tr>
          <th>Email</th>
          <th>Kind</th>
          {withSwitch ? <th style={{ width: "150px" }}>Sent by flow</th> : null}
          <th style={{ width: "180px" }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.kind}>
            <td className="table-card-title">
              {t.label}
              {t.edited === true ? (
                <>
                  {" "}
                  <EditedTag />
                </>
              ) : null}
            </td>
            <td data-label="Kind">
              <code>{t.kind}</code>
            </td>
            {withSwitch ? (
              <td data-label="Sent by flow">
                {t.flow !== undefined ? (
                  <FlowSwitchTags state={flowSwitch(t.flow, flows)} />
                ) : (
                  <span
                    className="subtle"
                    title="Sent on request, not by a flow — there is no on/off switch."
                  >
                    on request
                  </span>
                )}
              </td>
            ) : null}
            <td data-label="">
              {viewButton(t)}
              {t.editable ? editLink(t) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const newsletterTable = (rows: ReadonlyArray<Newsletter>): JSX.Element => (
    <table className="data-table table-cards">
      <thead>
        <tr>
          <th>Subject</th>
          <th style={{ width: "130px" }}>Sent</th>
          <th style={{ width: "110px" }}>Delivered</th>
          <th style={{ width: "100px" }}>Bounced</th>
          <th style={{ width: "110px" }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((n) => (
          <tr key={n.broadcast_id}>
            <td className="table-card-title">
              {n.subject.length > 0 ? n.subject : "(no subject)"}
            </td>
            <td data-label="Sent">{sentDate.format(new Date(n.first_seen_at))}</td>
            <td data-label="Delivered">{n.delivered.toLocaleString("en-GB")}</td>
            <td data-label="Bounced">{n.bounced.toLocaleString("en-GB")}</td>
            <td data-label="">
              <button
                type="button"
                className="row-action-btn"
                disabled={opening === n.broadcast_id}
                onClick={() => void onViewNewsletter(n)}
              >
                {opening === n.broadcast_id ? "Loading…" : "View"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const byCategory = (cat: EmailEntry["category"]): ReadonlyArray<EmailEntry> =>
    (templates ?? []).filter((t) => t.category === cat);
  const transactional = byCategory("transactional");
  const sharedLayout = byCategory("nurture");
  const auth = byCategory("auth");
  const sectionTitle = { fontSize: "16px", margin: "0 0 8px" } as const;
  const sectionNote = { maxWidth: "720px", margin: "0 0 10px" } as const;

  return (
    <div>
      <h1 className="page-title">Emails</h1>
      <p className="dc-muted" style={{ maxWidth: "720px" }}>
        Every email this studio sends: the ones Clavis builds from a template
        (view any of them as a member would get it), the plain-text alerts to
        staff, and the newsletters sent by hand. Emails written fresh each time
        — the assistant&apos;s replies (including the payment confirmation
        that ends an email conversation), approved outreach and inbox replies,
        poll and event invitations — have no template and are not listed.
      </p>

      {loading ? <p className="dc-muted">Loading…</p> : null}
      {listError !== null ? (
        <p className="action-modal-error">{listError}</p>
      ) : null}
      {openError !== null ? (
        <p className="action-modal-error">{openError}</p>
      ) : null}
      {list !== null && !list.saved_edits_read ? (
        <p className="action-modal-error" style={{ maxWidth: "720px" }}>
          This studio&apos;s saved email edits could not be read, so no email
          is marked &ldquo;edited&rdquo; here and View may fail — it never
          shows the built-in version in place of one it could not check.
          Reload to try again.
        </p>
      ) : null}

      {templates !== null && transactional.length > 0 ? (
        <section style={{ marginTop: "22px" }}>
          <h2 style={sectionTitle}>Transactional</h2>
          <p className="dc-muted" style={sectionNote}>
            Sent the moment something happens. Each one is switched on or off
            on <Link to="/flows">Flows</Link>.
          </p>
          {templateTable(transactional, true)}
        </section>
      ) : null}

      {templates !== null && (marketing.length > 0 || sharedLayout.length > 0) ? (
        <section style={{ marginTop: "22px" }}>
          <h2 style={sectionTitle}>Marketing</h2>
          <p className="dc-muted" style={sectionNote}>
            The email steps of each marketing flow, in the order a member
            receives them. A flow that is off sends none of its emails.
          </p>
          {list !== null && !list.flow_steps_read ? (
            <p className="action-modal-error" style={sectionNote}>
              This studio&apos;s flow steps could not be read, so only the
              emails with their own design are listed. A step that uses the
              shared layout may be missing here — check it on{" "}
              <Link to="/flows">Flows</Link>.
            </p>
          ) : null}
          {marketing.map((group) => (
            <div key={group.flow} style={{ marginBottom: "16px" }}>
              <p style={{ margin: "0 0 6px" }}>
                <code>{group.flow}</code>{" "}
                <FlowSwitchTags state={flowSwitch(group.flow, flows)} />
                <Link to={`/flows/${group.flow}/edit`}>Open flow</Link>
              </p>
              <table className="data-table table-cards">
                <thead>
                  <tr>
                    <th style={{ width: "70px" }}>Step</th>
                    <th>Email</th>
                    <th style={{ width: "150px" }}>Layout</th>
                    <th style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.emails.map((t) => (
                    <tr key={`${t.kind}:${t.layout ?? ""}`}>
                      <td data-label="Step">{t.step ?? "—"}</td>
                      <td className="table-card-title">
                        {t.label}
                        {t.edited === true ? (
                          <>
                            {" "}
                            <EditedTag />
                          </>
                        ) : null}
                        {t.in_flow === false ? (
                          <>
                            {" "}
                            <span
                              className="tag"
                              title="The flow no longer has an email step at this position, so this design is not being sent."
                            >
                              not in the current flow
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td data-label="Layout">
                        {t.layout !== undefined ? LAYOUT_LABEL[t.layout] : "—"}
                      </td>
                      <td data-label="">
                        {t.previewable ? viewButton(t) : null}
                        {t.editable ? (
                          editLink(t)
                        ) : (
                          <Link
                            className="row-action-btn"
                            style={
                              t.previewable ? { marginLeft: "8px" } : undefined
                            }
                            to={`/flows/${group.flow}/edit`}
                            title="This step has no design of its own: its subject and copy are written in the flow itself."
                          >
                            Edit copy
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {sharedLayout.length > 0 ? (
            <div>
              <p className="dc-muted" style={sectionNote}>
                The shared layout wraps the subject and copy of any marketing
                step that has no design of its own.
              </p>
              {templateTable(sharedLayout, false)}
            </div>
          ) : null}
        </section>
      ) : null}

      {templates !== null && auth.length > 0 ? (
        <section style={{ marginTop: "22px" }}>
          <h2 style={sectionTitle}>Sign-in &amp; verification</h2>
          {templateTable(auth, false)}
        </section>
      ) : null}

      <section style={{ marginTop: "22px" }}>
        <h2 style={sectionTitle}>Staff alerts</h2>
        <p className="dc-muted" style={sectionNote}>
          Plain-text emails to staff, written when they are sent, so there is
          no template to preview. Who gets which alert, and on which channel,
          is each person&apos;s own setting on <Link to="/config">Config</Link>{" "}
          (staff roster, Settings). The count is who an alert of that type
          would email right now.
        </p>
        {liveError !== null ? (
          <p className="action-modal-error">
            Staff alerts and newsletters could not be loaded ({liveError}).
          </p>
        ) : null}
        {live !== null ? (
          <table className="data-table table-cards">
            <thead>
              <tr>
                <th style={{ width: "260px" }}>Alert</th>
                <th>What it covers</th>
                <th style={{ width: "130px" }}>By email now</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.type}>
                  <td className="table-card-title">{a.label}</td>
                  <td data-label="What it covers" className="dc-muted table-card-prose">
                    {a.hint}
                  </td>
                  <td data-label="By email now">{peopleCount(a.byEmail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      <section style={{ marginTop: "22px" }}>
        <h2 style={sectionTitle}>Newsletter</h2>
        <p className="dc-muted" style={sectionNote}>
          Sent by hand from Resend, not by Clavis. Listed from Resend&apos;s
          delivery reports, newest first. How each one did, 6 hours to 14 days
          after sending, is on <Link to="/campaigns">Campaigns</Link>.
        </p>
        {live !== null && newsletters.sent.length === 0 ? (
          <p className="dc-muted">No newsletter has been delivered yet.</p>
        ) : null}
        {newsletters.sent.length > 0 ? newsletterTable(newsletters.sent) : null}
        {newsletters.tests.length > 0 ? (
          <details style={{ marginTop: "10px" }}>
            <summary className="dc-muted">
              Test sends — reached fewer than {NEWSLETTER_TEST_SEND_BELOW}{" "}
              people ({newsletters.tests.length})
            </summary>
            {newsletterTable(newsletters.tests)}
          </details>
        ) : null}
      </section>

      {preview !== null ? (
        <EmailPreviewModal
          title={preview.title}
          subtitle={preview.subtitle}
          {...(preview.subject !== undefined ? { subject: preview.subject } : {})}
          html={preview.html}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </div>
  );
}
